import React, { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Heart, CheckCircle, Sparkle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [5, 10, 15, 25];

/**
 * TipCard — buyer-facing optional tip panel.
 * Renders when: order.status in {delivered, completed} AND buyer is viewing AND no paid tip exists.
 * Hides automatically after a successful tip and shows a tasteful confirmation.
 *
 * Props:
 *   order        — order doc (must include id, seller_id, status)
 *   sellerName   — display name of the seller
 *   existingTip  — null OR the most recent tip doc (used to render confirmation state)
 *   onTipPaid    — callback after a successful tip session redirect
 *   feePct       — platform fee percent from /api/settings/public (defaults to 17)
 */
export default function TipCard({ order, sellerName, existingTip, feePct = 17 }) {
  const [selected, setSelected] = useState(10);
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const [declined, setDeclined] = useState(false);

  // 30-day tip window: starts at order.completed_at (or approved_at), or now if
  // status is still "delivered" (timer hasn't started). Server-side enforces too.
  const windowClosed = useMemo(() => {
    const ts = order?.completed_at || order?.approved_at;
    if (!ts) return false;  // delivered-but-not-yet-completed: window not started
    const completed = new Date(ts).getTime();
    const elapsed = (Date.now() - completed) / (1000 * 60 * 60 * 24);
    return elapsed > 30;
  }, [order?.completed_at, order?.approved_at]);

  const amount = useMemo(() => {
    if (custom !== "" && !Number.isNaN(parseFloat(custom))) return parseFloat(custom);
    return selected;
  }, [custom, selected]);

  const valid = amount >= 5 && amount <= 500;
  const fee = useMemo(() => Math.round(amount * feePct) / 100, [amount, feePct]);
  const sellerNet = useMemo(() => Math.round((amount - fee) * 100) / 100, [amount, fee]);

  // ----- 30-day tip window has closed (and no paid tip exists) — hide entirely -----
  if (windowClosed && !(existingTip && existingTip.status === "paid")) {
    return null;
  }

  // ----- Already-tipped (paid) state: tasteful confirmation -----
  if (existingTip && existingTip.status === "paid") {  return (
      <div className="mt-6 p-5 rounded-2xl bg-mv-primary/5 border border-mv-primary/30"
           data-testid="tip-confirmation">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-mv-primary/15 flex items-center justify-center shrink-0">
            <CheckCircle size={20} weight="duotone" className="text-mv-primary" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-mv-secondary">
              Tip sent — thank you
            </div>
            <div className="text-sm text-gray-600 mt-0.5">
              You tipped ${Number(existingTip.gross_tip_amount).toFixed(2)} to {sellerName}. ${Number(existingTip.seller_tip_earnings).toFixed(2)} went to them.
            </div>
            <a href={`/orders/${order.id}#tip-${existingTip.id}`}
               className="text-xs text-mv-primary font-bold mt-2 inline-block"
               data-testid="tip-receipt-link">
              View receipt
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ----- Buyer chose "No thanks" — collapse without pressuring -----
  if (declined) {
    return (
      <div className="mt-6 text-xs text-gray-400" data-testid="tip-declined">
        Tip option dismissed. You can still tip from this page until the order
        completes.
      </div>
    );
  }

  const submit = async () => {
    if (!valid) {
      toast.error("Tip must be between $5 and $500");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/tips/checkout`,
        { order_id: order.id, amount },
        {
          headers: { "Content-Type": "application/json", Origin: window.location.origin },
        });
      // Redirect to Stripe Checkout. Real path = stripe.com URL; mock = emergent edge.
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Could not start tip checkout");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tip checkout failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6 p-5 sm:p-6 rounded-2xl bg-white border border-gray-200 shadow-sm"
         data-testid="tip-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-mv-accent/20 flex items-center justify-center shrink-0">
          <Heart size={20} weight="duotone" className="text-mv-primary" />
        </div>
        <div>
          <div className="font-display font-bold text-mv-secondary text-lg leading-tight"
               data-testid="tip-heading">
            Show your appreciation to {sellerName}
          </div>
          <div className="text-sm text-gray-600 mt-1 leading-relaxed">
            Reward great work with an optional tip. MarketVyne applies a {feePct}% platform fee,
            and the seller receives {100 - feePct}%.
          </div>
        </div>
      </div>

      {/* Preset amounts + custom */}
      <div className="grid grid-cols-4 gap-2" data-testid="tip-presets">
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => { setSelected(p); setCustom(""); }}
            className={`px-3 py-2.5 rounded-xl text-sm font-display font-bold border transition ${
              custom === "" && selected === p
                ? "bg-mv-secondary text-white border-mv-secondary"
                : "bg-white text-mv-secondary border-gray-200 hover:border-mv-primary/50"
            }`}
            data-testid={`tip-preset-${p}`}
          >
            ${p}
          </button>
        ))}
      </div>

      <div className="mt-3">
        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">
          Custom amount
        </label>
        <div className="relative mt-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
          <Input
            type="number"
            inputMode="decimal"
            min={5}
            max={500}
            step="0.01"
            placeholder="5.00 – 500.00"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            className="pl-7"
            data-testid="tip-custom-input"
          />
        </div>
      </div>

      {/* Live breakdown */}
      <div className="mt-4 p-3 rounded-xl bg-gray-50 text-sm" data-testid="tip-breakdown">
        <div className="flex justify-between text-gray-700">
          <span>You pay</span>
          <span className="font-display font-black text-mv-secondary" data-testid="tip-buyer-pays">
            ${amount.toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between text-gray-500 mt-0.5">
          <span>MarketVyne fee ({feePct}%)</span>
          <span data-testid="tip-platform-fee">${fee.toFixed(2)}</span>
        </div>
        <div className="flex justify-between mt-1 pt-2 border-t border-gray-200">
          <span className="font-bold text-mv-primary">Seller receives</span>
          <span className="font-display font-black text-mv-primary"
                data-testid="tip-seller-receives">
            ${sellerNet.toFixed(2)}
          </span>
        </div>
      </div>

      {!valid && (
        <div className="mt-2 text-xs text-rose-600" data-testid="tip-range-error">
          Tip must be between $5 and $500.
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          onClick={() => setDeclined(true)}
          className="font-bold border-gray-300"
          data-testid="tip-decline-btn"
        >
          No thanks
        </Button>
        <Button
          disabled={!valid || loading}
          onClick={submit}
          className="bg-mv-primary hover:bg-mv-primary/90 text-white font-bold flex-1 sm:flex-none sm:ml-auto"
          data-testid="tip-send-btn"
        >
          <Sparkle size={16} className="mr-1.5" weight="fill" />
          {loading ? "Opening checkout…" : `Send $${amount.toFixed(2)} tip`}
        </Button>
      </div>

      <div className="mt-3 text-[11px] text-gray-400 leading-relaxed">
        Tips do not purchase additional work, revisions, priority, or scope changes.
        See our <a href="/policies#tips" className="underline text-gray-500">tipping policy</a>.
      </div>
    </div>
  );
}
